import * as React from 'react';
import { Map } from '@cian/frontend-newbuilding-map-component';
import { useApplicationContext } from '../../../utils/applicationContext';
import { ICoordinates, IGeoObjectBalloonProps, YANDEX_API_KEY, YANDEX_SUGGEST_API_KEY } from '../utils';
import { IControl } from '@cian/frontend-newbuilding-map-component/es/types/control';

interface IMapContainerProps {
  centeredCoordinates: ICoordinates;
  geoObjectNames: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onGeoObjectsFetch: () => Promise<any[]>;
  onBalloonHtmlFetch: IGeoObjectBalloonProps['onBalloonHtmlFirstFetch'];
  controls: IControl[];

  id?: string;
  mapRef?: React.MutableRefObject<Map | null>;
  zoom?: number;
  suggestPosition?: 'top' | 'bottom';
  onRulerClick?: () => void;
  onMapLoaded?: () => void;
  onBalloonOpen?: IGeoObjectBalloonProps['onBalloonOpen'];
  onFullscreenEnter?: () => void;
  onFullscreenExit?: () => void;
  onZoomControlClick?: () => void;
  onRoutingStart?: () => void;
  onRoutingEnd?: () => void;
  onRouteBuildingSuccess?: () => void;
  onNoPanoramaEnter?: () => void;
  onPanoramaExit?: () => void;
  onPanoramaSuccessEnter?: () => void;
}

export const InfrastructureMapContainer: React.FC<IMapContainerProps> = ({
  centeredCoordinates,
  geoObjectNames,
  onGeoObjectsFetch,
  onBalloonHtmlFetch,
  id,
  mapRef,
  onBalloonOpen,
  onMapLoaded,
  onFullscreenEnter,
  onFullscreenExit,
  onNoPanoramaEnter,
  onPanoramaExit,
  onPanoramaSuccessEnter,
  controls,
  onRoutingStart,
  onRoutingEnd,
  onRouteBuildingSuccess,
  suggestPosition = 'bottom',
  zoom = 15,
}) => {
  const { config } = useApplicationContext();
  const apiKey = config.getStrict<string>(YANDEX_API_KEY);
  const suggestApiKey = config.getStrict<string>(YANDEX_SUGGEST_API_KEY);

  const [isMapLoaded, setIsMapLoaded] = React.useState(false);

  const setMapRef = React.useCallback(
    (node: Map | null) => {
      if (isMapLoaded && mapRef) {
        mapRef.current = node;
      }
    },
    [isMapLoaded, mapRef],
  );

  const handleMapLoaded = React.useCallback(() => {
    setIsMapLoaded(true);
    onMapLoaded?.();
  }, [onMapLoaded]);

  return (
    <Map
      ref={setMapRef}
      media={[
        {
          minWidth: -Infinity,
          maxWidth: Infinity,
          options: {
            center: centeredCoordinates,
            styles: { width: '100%', height: '100%' },
            animationDuration: 300,
            defaultZoom: zoom,
          },
        },
      ]}
      geoObjectNames={geoObjectNames}
      firstPaintOptions={{
        disableScrollZoom: true,
        displayRouteInfo: true,
        fetch: {
          onGeoObjectsFetch,
          onBalloonHtmlFetch,
        },
        id,
        styles: { width: '100%', height: '100%' },
        zoomRange: { min: 1, max: 17 },
        controls,
        apiKey,
        suggestApiKey,
        onBalloonOpen,
        onMapSuccess: handleMapLoaded,
        onFullscreenEnter,
        onFullscreenExit,
        onNoPanoramaEnter,
        onPanoramaExit,
        onPanoramaSuccessEnter,
        onRoutingStart,
        onRoutingEnd,
        onRouteBuildingSuccess,
        suggestPosition,
      }}
    />
  );
};
